import { LAYOUTS } from '@/constants/layouts.constant'
import type { AppRoutes } from '@/types/routes.types'

const ROUTE_NODE_TYPE = {
  NORMAL: 'normal',
  DYNAMIC: 'dynamic',
  OPTIONAL: 'optional',
  REGEX: 'regex',
  CATCH_ALL: 'catch-all',
} as const


const APP_ROUTES: AppRoutes = {
  HOME: {
    name: 'home.route',
    path: '/',
    component: 'modules/home/view/HomeView.vue',
    meta: {
      titleKey: 'common.home',
      icon: 'LayoutDashboard',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  CUSTOMERS_LIST: {
    name: 'customers.list.route',
    path: '/customers',
    component: 'modules/customers/view/CustomerListView.vue',
    meta: {
      titleKey: 'menu.customer_list',
      icon: 'Contact',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  CUSTOMERS_DETAIL: {
    name: 'customers.detail.route',
    path: '/customers/:id',
    type: ROUTE_NODE_TYPE.DYNAMIC,
    component: 'modules/home/view/HomeView.vue',
    meta: {
      titleKey: 'menu.customer_detail',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
      hidden: true,
    },
  },

  AGENTS_LIST: {
    name: 'agents.list.route',
    path: '/agents',
    component: 'modules/agents/view/AgentListView.vue',
    meta: {
      titleKey: 'menu.agent_list',
      icon: 'Handshake',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  AGENTS_DETAIL: {
    name: 'agents.detail.route',
    path: '/agents/:id',
    type: ROUTE_NODE_TYPE.DYNAMIC,
    component: 'modules/home/view/HomeView.vue',
    meta: {
      titleKey: 'menu.agent_detail',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
      hidden: true,
    },
  },

  CUSTOMERS_GROUPS: {
    name: 'customers.groups.route',
    path: '/groups',
    component: 'modules/customer-groups/view/GroupListView.vue',
    meta: {
      titleKey: 'menu.customer_groups',
      icon: 'Users',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  CAMPAIGNS_LIST: {
    name: 'campaigns.list.route',
    path: '/campaigns',
    component: 'modules/campaigns/view/CampaignListView.vue',
    meta: {
      titleKey: 'menu.campaign_management',
      icon: 'Megaphone',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  CAMPAIGNS_DETAIL: {
    name: 'campaigns.detail.route',
    path: '/campaigns/:id',
    type: ROUTE_NODE_TYPE.DYNAMIC,
    component: 'modules/home/view/HomeView.vue',
    meta: {
      titleKey: 'menu.campaign_detail',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
      hidden: true,
    },
  },

  ORDERS_LIST: {
    name: 'orders.list.route',
    path: '/orders',
    component: 'modules/orders/view/OrderListView.vue',
    meta: {
      titleKey: 'menu.order_management',
      icon: 'ShoppingCart',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
  },

  ORDERS_DETAIL: {
    name: 'orders.detail.route',
    path: '/orders/:id',
    type: ROUTE_NODE_TYPE.DYNAMIC,
    component: 'modules/home/view/HomeView.vue',
    meta: {
      titleKey: 'menu.order_detail',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
      hidden: true,
    },
  },


  SYSTEM: {
    name: 'system.route',
    path: '/system',
    meta: {
      titleKey: 'menu.system_management',
      descriptionKey: 'menu.system_management_desc',
      icon: 'Settings',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
      isDirectory: true,
    },
    children: {
      USERS: {
        name: 'users.route',
        path: 'users',
        redirectTo: 'users.list.route',
        meta: {
          titleKey: 'menu.account_management',
          descriptionKey: 'menu.account_management_desc',
          icon: 'UserCog',
          themeColor: 'text-blue-500',
          themeBg: 'bg-blue-500/10',
          themeBorder: 'border-blue-500/20',
        },
        children: {
          LIST: {
            name: 'users.list.route',
            path: 'list',
            component: 'modules/users/view/UserListView.vue',
            meta: {
              titleKey: 'menu.system_accounts',
              icon: 'User',
            },
          },
          ROLES: {
            name: 'users.roles.route',
            path: 'roles',
            component: 'modules/users/view/RoleListView.vue',
            meta: {
              titleKey: 'menu.roles',
              icon: 'ShieldCheck',
            },
          },
          DETAIL: {
            name: 'users.detail.route',
            path: ':id',
            type: ROUTE_NODE_TYPE.DYNAMIC,
            component: 'modules/home/view/HomeView.vue',
            meta: {
              titleKey: 'menu.users_detail',
              hidden: true,
            },
          },
        },
      },
      EMAIL_CONFIG: {
        name: 'system.email_config.route',
        path: 'email-config',
        redirectTo: 'system.email_templates.route',
        meta: {
          titleKey: 'menu.email_config',
          descriptionKey: 'menu.email_config_desc',
          icon: 'Mails',
          themeColor: 'text-amber-500',
          themeBg: 'bg-amber-500/10',
          themeBorder: 'border-amber-500/20',
        },
        children: {
          EMAIL_TEMPLATES: {
            name: 'system.email_templates.route',
            path: 'email-templates',
            component: 'modules/email-templates/view/EmailTemplateListView.vue',
            meta: {
              titleKey: 'menu.email_templates',
              icon: 'Mail',
            },
          },
          EMAIL_TEMPLATE_VARS: {
            name: 'system.email_template_vars.route',
            path: 'email-template-vars',
            component: 'modules/email-template-vars/view/TemplateVarListView.vue',
            meta: {
              titleKey: 'menu.email_template_vars',
              icon: 'Hash',
            },
          },
        },
      },
      CATEGORIES: {
        name: 'categories.route',
        path: 'categories',
        redirectTo: 'categories.list.route',
        meta: {
          titleKey: 'menu.category_management',
          descriptionKey: 'menu.category_management_desc',
          icon: 'Tag',
          themeColor: 'text-purple-500',
          themeBg: 'bg-purple-500/10',
          themeBorder: 'border-purple-500/20',
        },
        children: {
          LIST: {
            name: 'categories.list.route',
            path: 'list',
            component: 'modules/categories/view/CategoryListView.vue',
            meta: {
              titleKey: 'menu.category_list',
              icon: 'Tag',
            },
          },
        },
      },
    },
  },

  PROFILE: {
    name: 'profile.route',
    path: '/profile',
    redirectTo: 'profile.info.route',
    meta: {
      titleKey: 'common.profile',
      icon: 'UserCircle',
      layout: LAYOUTS.DEFAULT,
      requiresAuth: true,
    },
    children: {
      INFO: {
        name: 'profile.info.route',
        path: 'info',
        component: 'modules/profile/view/ProfileInfoView.vue',
        meta: {
          titleKey: 'menu.profile_info',
          icon: 'IdCard',
        },
      },
      EDIT: {
        name: 'profile.edit.route',
        path: 'edit',
        component: 'modules/profile/view/ProfileEditView.vue',
        meta: {
          titleKey: 'menu.edit_profile',
          icon: 'UserPen',
        },
      },
    },
  },

  AUTH: {
    name: 'auth.route',
    path: '/auth',
    redirectTo: 'auth.login.route',
    meta: {
      hidden: true,
      layout: LAYOUTS.AUTH,
      requiresAuth: false,
    },
    children: {
      LOGIN: {
        name: 'auth.login.route',
        path: 'login',
        component: 'modules/auth/view/LoginView.vue',
        meta: {
          titleKey: 'auth.login',
        },
      },
      REGISTER: {
        name: 'auth.register.route',
        path: 'register',
        component: 'modules/auth/view/LoginView.vue',
        meta: {
          titleKey: 'auth.register',
        },
      },
    },
  },

  ERRORS: {
    name: 'errors.route',
    path: '/errors',
    redirectTo: 'errors.not_found.route',
    meta: {
      hidden: true,
      layout: LAYOUTS.AUTH,
      requiresAuth: false,
    },
    children: {
      NOT_FOUND: {
        name: 'errors.not_found.route',
        path: '404',
        component: 'modules/errors/view/NotFoundView.vue',
        meta: {
          titleKey: 'common.not_found',
        },
      },
      FORBIDDEN: {
        name: 'errors.forbidden.route',
        path: '403',
        component: 'modules/errors/view/ForbiddenView.vue',
        meta: {
          titleKey: 'common.forbidden',
        },
      },
      SERVER: {
        name: 'errors.server.route',
        path: '500',
        component: 'modules/errors/view/ServerErrorView.vue',
        meta: {
          titleKey: 'common.server_error',
        },
      },
    },
  },

  CATCH_ALL: {
    name: 'catch_all.route',
    path: '/:pathMatch(.*)*',
    type: ROUTE_NODE_TYPE.CATCH_ALL,
    redirectTo: 'errors.not_found.route',
    meta: {
      hidden: true,
    },
  },
}

export {
  ROUTE_NODE_TYPE,
  APP_ROUTES
}